import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debugLog } from '../../utils/debugLog';
import {
  ReviewAngle,
  angleCovers,
  angleSourceMs,
  driftCorrection,
  hasKnownLength,
  soundSourceAt,
  sourceEndMs,
  timelineLengthMs,
  withMeasuredDuration,
} from './multicamReview';

/**
 * Every angle of a multicam set playing at once on one shared clock: the
 * engine behind the editor's review player and the studio's live cut
 * multiview. Each angle has its own <video>; nothing is ever swapped or
 * reloaded to show another angle.
 *
 * Sync rules (MC-31): an angle that is seeking or has no data yet is left
 * alone (re-seeking it every frame cancelled its range request, so on slow
 * networks it never started); small drift is corrected by nudging playback
 * speed, large drift by a seek; and the clock holds while the audio angle
 * buffers, so the picture waits for the sound instead of running ahead.
 *
 * A set with a separate sound recording plays it in an <audio> element
 * (`audioSourceRef`), which then is the sound and the clock in place of the
 * audio angle, kept in step by the same rules; every video is muted.
 *
 * Where that sound has no footage (before it starts, after it ends, or for
 * clips placed one after another) the angle on screen is heard instead, else
 * the first angle that has footage (see soundSourceAt); it is then the clock.
 * Exactly one element is ever audible.
 */

export type TileState = 'loading' | 'ready' | 'buffering' | 'error';

/** Playback started or stopped, or the playhead jumped. */
export type TransportEvent = 'play' | 'pause' | 'seek' | 'end';

/** Re-render at most this often while playing; the videos run independently. */
const RENDER_INTERVAL_MS = 50;

const nextFrame = (callback: () => void): number =>
  typeof window.requestAnimationFrame === 'function'
    ? window.requestAnimationFrame(callback)
    : window.setTimeout(callback, 16);

const cancelFrame = (handle: number) => {
  if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(handle);
  else window.clearTimeout(handle);
};

/** The key a separate sound recording goes by inside the engine (never an angle's). */
export const RECORDING_KEY = '__recording';

/** A media element's own length in seconds, or Infinity while it is unknown. */
const fileLengthS = (el: HTMLMediaElement): number =>
  Number.isFinite(el.duration) && el.duration > 0 ? el.duration : Infinity;

export interface SyncedAnglesOptions<A extends ReviewAngle> {
  angles: A[];
  audioAngleKey?: string | null;
  /** A separate sound recording (timed like an angle): it is the sound and the clock. */
  audioSource?: ReviewAngle | null;
  timelineDurationMs?: number | null;
  /** Where the clock starts, in timeline ms (read once, on mount). */
  initialTimeMs?: number;
  muted: boolean;
  /**
   * The angle on screen (the live angle of the cut, or the one being viewed).
   * Where the set's own sound has no footage, this angle is heard if it has.
   */
  onScreenAngleKey?: string | null;
  /** Volume (0–1) applied to every angle; left alone when omitted. */
  volume?: number;
  speed: number;
  /** Reports the shared clock (throttled while playing), e.g. to drive a timeline playhead. */
  onTimeChange?: (timeMs: number, playing: boolean) => void;
  onTransport?: (event: TransportEvent) => void;
}

export interface SyncedAngles<A extends ReviewAngle> {
  /** Sorted by key, with lengths measured from the media filled in. */
  sortedAngles: A[];
  lengthMs: number;
  /** The angle heard; undefined when a separate recording is. */
  audioAngle: A | undefined;
  /** The separate recording, with its measured length, when there is one. */
  audioSource: ReviewAngle | null;
  /** The clock as last rendered (throttled while playing). */
  currentMs: number;
  playing: boolean;
  tiles: Record<string, TileState>;
  videoRefs: MutableRefObject<Record<string, HTMLVideoElement | null>>;
  setTile: (key: string, state: TileState) => void;
  play: () => void;
  pause: () => void;
  /** Pauses on the moment showing now, read from the clock rather than the last frame drawn. */
  pauseExact: () => void;
  seek: (timeMs: number) => void;
  isPlaying: () => boolean;
  /** The clock now: from the audio angle while it plays, so it is exact rather than throttled. */
  getTimeMs: () => number;
  /** Whether an angle has footage at `seconds`, using measured lengths. */
  covers: (angleKey: string, seconds: number) => boolean;
  /** The key of what is heard at `timeMs` (an angle, or RECORDING_KEY); null when nothing has footage. */
  soundKeyAt: (timeMs: number) => string | null;
  /** Re-renders at the clock's exact time, e.g. right after a cut. */
  refresh: () => void;
  handleLoadedMetadata: (angle: A, el: HTMLVideoElement) => void;
  retryAngle: (angle: A) => void;
  /** Ref for the <audio> that plays the separate recording. */
  audioSourceRef: (el: HTMLAudioElement | null) => void;
  handleAudioSourceLoadedMetadata: (el: HTMLAudioElement) => void;
}

export function useSyncedAngles<A extends ReviewAngle>({
  angles,
  audioAngleKey,
  audioSource,
  timelineDurationMs,
  initialTimeMs = 0,
  muted,
  onScreenAngleKey,
  volume,
  speed,
  onTimeChange,
  onTransport,
}: SyncedAnglesOptions<A>): SyncedAngles<A> {
  // Real lengths from loadedmetadata, for angles the snapshot did not measure.
  const [measured, setMeasured] = useState<Record<string, number>>({});
  const sortedAngles = useMemo(
    () => [...angles]
      .sort((a, b) => a.angleKey.localeCompare(b.angleKey))
      .map((angle) => withMeasuredDuration(angle, measured[angle.angleKey]) as A),
    [angles, measured]
  );
  const lengthMs = useMemo(
    () => timelineLengthMs(sortedAngles, timelineDurationMs),
    [sortedAngles, timelineDurationMs]
  );
  const recording = useMemo(
    () => (audioSource?.videoUrl
      ? withMeasuredDuration({ ...audioSource, angleKey: RECORDING_KEY }, measured[RECORDING_KEY])
      : null),
    [audioSource, measured]
  );
  const audioAngle = recording ? undefined : sortedAngles.find((a) => a.angleKey === audioAngleKey) ?? sortedAngles[0];
  // What is heard and keeps time: the recording, or the audio angle.
  const sound: ReviewAngle | undefined = recording ?? audioAngle;

  const [currentMs, setCurrentMs] = useState(initialTimeMs);
  const [playing, setPlaying] = useState(false);
  const [tiles, setTiles] = useState<Record<string, TileState>>({});

  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const recordingElRef = useRef<HTMLAudioElement | null>(null);
  const recordingRef = useRef(recording);
  recordingRef.current = recording;
  /** The element playing an angle, or the recording. */
  const elementFor = useCallback(
    (key: string): HTMLMediaElement | null => (key === RECORDING_KEY ? recordingElRef.current : videoRefs.current[key] ?? null),
    []
  );
  const clock = useRef({ anchorMs: initialTimeMs, anchorAt: 0 });
  const currentRef = useRef(initialTimeMs);
  const playingRef = useRef(false);
  const speedRef = useRef(1);
  const frameRef = useRef<number | null>(null);
  const lastRenderRef = useRef(0);
  const anglesRef = useRef(sortedAngles);
  anglesRef.current = sortedAngles;
  const soundRef = useRef(sound);
  soundRef.current = sound;
  const onScreenRef = useRef(onScreenAngleKey);
  onScreenRef.current = onScreenAngleKey;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const lengthRef = useRef(lengthMs);
  lengthRef.current = lengthMs;
  const onTimeChangeRef = useRef(onTimeChange);
  onTimeChangeRef.current = onTimeChange;
  const onTransportRef = useRef(onTransport);
  onTransportRef.current = onTransport;

  /** What is heard at timeline time `t` (see soundSourceAt). */
  const heardAt = useCallback(
    (t: number): ReviewAngle | null => soundSourceAt(soundRef.current, anglesRef.current, t, onScreenRef.current),
    []
  );

  /** Only the heard element is audible; React does not reliably apply `muted`. */
  const applyMuting = useCallback((heardKey: string | null | undefined) => {
    for (const angle of anglesRef.current) {
      const el = videoRefs.current[angle.angleKey];
      const wanted = mutedRef.current || angle.angleKey !== heardKey;
      if (el && el.muted !== wanted) el.muted = wanted;
    }
    const recordingEl = recordingElRef.current;
    if (recordingEl) {
      const wanted = mutedRef.current || !recordingRef.current || heardKey !== RECORDING_KEY;
      if (recordingEl.muted !== wanted) recordingEl.muted = wanted;
    }
  }, []);

  const setTile = useCallback((key: string, state: TileState) => {
    setTiles((previous) => (previous[key] === state ? previous : { ...previous, [key]: state }));
  }, []);

  const startPlayback = useCallback((angle: ReviewAngle, el: HTMLMediaElement) => {
    const result = el.play() as Promise<void> | undefined;
    if (result && typeof result.catch === 'function') {
      result.catch((error: unknown) => {
        const name = (error as { name?: string })?.name;
        // AbortError: a pause or seek interrupted play(); that is expected.
        if (name === 'AbortError') return;
        debugLog(`Angle ${angle.angleKey} could not start:`, error);
        if (name === 'NotSupportedError') setTile(angle.angleKey, 'error');
      });
    }
  }, [setTile]);

  /** Puts one angle at timeline time `t`; it plays while playing and covered. */
  const syncAngle = useCallback(
    (angle: ReviewAngle, t: number, isPlaying: boolean, force: boolean, isClock: boolean) => {
      const el = elementFor(angle.angleKey);
      if (!el) return;
      const covers = angleCovers(angle, t);
      const end = sourceEndMs(angle);
      const sourceMs = Math.max(angleSourceMs(angle, t), angle.sourceInMs || 0);
      const wantedS = (end === null ? sourceMs : Math.min(sourceMs, end)) / 1000;
      // A file can end before its stored length says (recordings' lengths
      // used to be rounded up to the second). Past its own end it is held
      // there rather than chased.
      const fileEndS = fileLengthS(el);
      const targetS = Math.min(wantedS, fileEndS);

      // The clock angle is never corrected mid-stream (the clock follows it).
      if (!isClock || force) {
        const correction = driftCorrection(el, targetS, { force, playing: isPlaying && covers });
        if (correction.action === 'seek') {
          try {
            if (el.readyState >= 1 || force) el.currentTime = targetS;
          } catch {
            // Not seekable before metadata loads; onLoadedMetadata syncs it.
          }
          el.playbackRate = speedRef.current;
        } else if (correction.action === 'nudge') {
          el.playbackRate = speedRef.current * correction.rate;
        } else if (correction.action === 'none') {
          el.playbackRate = speedRef.current;
        }
      }

      // play() on an ended element starts it again from the top, and the
      // clock follows the sound, so a file that has run out never plays.
      const runOut = wantedS >= fileEndS || el.ended;
      if (isPlaying && covers && !runOut) {
        if (el.paused) startPlayback(angle, el);
      } else if (!el.paused) {
        el.pause();
      }
    },
    [elementFor, startPlayback]
  );

  /** Syncs every angle (and the recording). `holdFollowers` pauses non-clock ones (audio buffering). */
  const syncVideos = useCallback(
    (t: number, isPlaying: boolean, force: boolean, holdFollowers = false) => {
      // The heard source is the clock; it changes where its footage ends.
      const clockKey = heardAt(t)?.angleKey ?? null;
      applyMuting(clockKey);
      const media: ReviewAngle[] = recordingRef.current ? [...anglesRef.current, recordingRef.current] : anglesRef.current;
      for (const angle of media) {
        const isClock = angle.angleKey === clockKey && isPlaying && angleCovers(angle, t);
        syncAngle(angle, t, isPlaying && (!holdFollowers || isClock), force, isClock);
      }
    },
    [applyMuting, heardAt, syncAngle]
  );

  const stopLoop = () => {
    if (frameRef.current !== null) cancelFrame(frameRef.current);
    frameRef.current = null;
  };

  const tick = useCallback(() => {
    if (!playingRef.current) return;
    const now = performance.now();
    let t = clock.current.anchorMs + (now - clock.current.anchorAt) * speedRef.current;
    let holding = false;

    // What is heard (the audio angle or the recording, else the angle that
    // has footage here) IS the clock, so the soundtrack is never corrected
    // mid-stream. If it is seeking or starved of data, hold the clock (and
    // the other angles) until it can play.
    const heard = heardAt(currentRef.current);
    const audioEl = heard ? elementFor(heard.angleKey) : null;
    // An angle that failed to load, or a file that has run out, cannot be
    // waited for; fall back to the wall clock.
    if (heard && audioEl && !audioEl.error && !audioEl.ended) {
      if (audioEl.seeking || audioEl.readyState < 3) {
        holding = true;
        t = clock.current.anchorMs;
      } else if (!audioEl.paused) {
        t = audioEl.currentTime * 1000 - (heard.sourceInMs || 0) + heard.timelineStartMs;
      }
    }

    const ended = lengthMs > 0 && t >= lengthMs;
    if (ended) t = lengthMs;
    clock.current = { anchorMs: t, anchorAt: now };
    currentRef.current = t;
    if (ended || now - lastRenderRef.current >= RENDER_INTERVAL_MS) {
      lastRenderRef.current = now;
      setCurrentMs(t);
      onTimeChangeRef.current?.(t, !ended);
    }

    if (ended) {
      playingRef.current = false;
      setPlaying(false);
      syncVideos(t, false, false);
      onTransportRef.current?.('end');
      return;
    }
    syncVideos(t, true, false, holding);
    frameRef.current = nextFrame(tick);
  }, [elementFor, heardAt, lengthMs, syncVideos]);

  const seek = useCallback(
    (timeMs: number) => {
      const t = Math.min(Math.max(0, timeMs), lengthMs > 0 ? lengthMs : Math.max(0, timeMs));
      currentRef.current = t;
      clock.current = { anchorMs: t, anchorAt: performance.now() };
      setCurrentMs(t);
      onTimeChangeRef.current?.(t, playingRef.current);
      syncVideos(t, playingRef.current, true);
      onTransportRef.current?.('seek');
    },
    [lengthMs, syncVideos]
  );

  const play = () => {
    if (lengthMs > 0 && currentRef.current >= lengthMs) seek(0);
    playingRef.current = true;
    setPlaying(true);
    clock.current = { anchorMs: currentRef.current, anchorAt: performance.now() };
    syncVideos(currentRef.current, true, false);
    stopLoop();
    frameRef.current = nextFrame(tick);
    onTransportRef.current?.('play');
  };

  const pause = () => {
    playingRef.current = false;
    setPlaying(false);
    stopLoop();
    syncVideos(currentRef.current, false, false);
    onTimeChangeRef.current?.(currentRef.current, false);
    onTransportRef.current?.('pause');
  };

  /** The clock now, without moving it (the same reading `tick` makes). */
  const getTimeMs = useCallback(() => {
    if (!playingRef.current) return currentRef.current;
    let t = clock.current.anchorMs + (performance.now() - clock.current.anchorAt) * speedRef.current;
    const heard = heardAt(currentRef.current);
    const soundEl = heard ? elementFor(heard.angleKey) : null;
    if (heard && soundEl && !soundEl.error && !soundEl.ended) {
      if (soundEl.seeking || soundEl.readyState < 3) t = clock.current.anchorMs;
      else if (!soundEl.paused) t = soundEl.currentTime * 1000 - (heard.sourceInMs || 0) + heard.timelineStartMs;
    }
    const length = lengthRef.current;
    return Math.max(0, length > 0 ? Math.min(t, length) : t);
  }, [elementFor, heardAt]);

  const pauseExact = () => {
    if (playingRef.current) {
      const t = getTimeMs();
      currentRef.current = t;
      clock.current = { anchorMs: t, anchorAt: performance.now() };
      setCurrentMs(t);
    }
    pause();
  };

  const isPlaying = useCallback(() => playingRef.current, []);

  const covers = useCallback((angleKey: string, seconds: number) => {
    const angle = anglesRef.current.find((candidate) => candidate.angleKey === angleKey);
    return Boolean(angle) && angleCovers(angle as A, seconds * 1000);
  }, []);

  const soundKeyAt = useCallback((timeMs: number) => heardAt(timeMs)?.angleKey ?? null, [heardAt]);

  const refresh = useCallback(() => setCurrentMs(getTimeMs()), [getTimeMs]);

  // Only what is heard at the playhead is audible (syncVideos keeps this up
  // to date while playing).
  useEffect(() => {
    applyMuting(heardAt(currentRef.current)?.angleKey);
  }, [muted, sortedAngles, audioAngle, recording, onScreenAngleKey, applyMuting, heardAt]);

  useEffect(() => {
    if (typeof volume !== 'number' || !Number.isFinite(volume)) return;
    const level = Math.min(1, Math.max(0, volume));
    for (const angle of sortedAngles) {
      const el = videoRefs.current[angle.angleKey];
      if (el) el.volume = level;
    }
    if (recordingElRef.current) recordingElRef.current.volume = level;
  }, [volume, sortedAngles, recording]);

  useEffect(() => {
    speedRef.current = speed;
    clock.current = { anchorMs: currentRef.current, anchorAt: performance.now() };
    for (const el of Object.values(videoRefs.current)) if (el) el.playbackRate = speed;
    if (recordingElRef.current) recordingElRef.current.playbackRate = speed;
  }, [speed]);

  useEffect(() => () => {
    playingRef.current = false;
    stopLoop();
    for (const el of Object.values(videoRefs.current)) el?.pause();
    recordingElRef.current?.pause();
  }, []);

  const handleLoadedMetadata = (angle: A, el: HTMLVideoElement) => {
    if (!hasKnownLength(angle) && Number.isFinite(el.duration) && el.duration > 0) {
      setMeasured((previous) => ({ ...previous, [angle.angleKey]: Math.round(el.duration * 1000) }));
    }
    // Only this angle: force-seeking every angle here restarted the others' loads.
    const current = anglesRef.current.find((a) => a.angleKey === angle.angleKey) ?? angle;
    syncAngle(current, currentRef.current, playingRef.current, true, false);
    setTile(angle.angleKey, el.readyState >= 3 ? 'ready' : 'loading');
  };

  const retryAngle = (angle: A) => {
    const el = videoRefs.current[angle.angleKey];
    if (!el) return;
    setTile(angle.angleKey, 'loading');
    el.load();
  };

  const audioSourceRef = useCallback((el: HTMLAudioElement | null) => {
    recordingElRef.current = el;
    if (el) el.playbackRate = speedRef.current;
  }, []);

  const handleAudioSourceLoadedMetadata = (el: HTMLAudioElement) => {
    const current = recordingRef.current;
    if (!current) return;
    if (!hasKnownLength(current) && Number.isFinite(el.duration) && el.duration > 0) {
      setMeasured((previous) => ({ ...previous, [RECORDING_KEY]: Math.round(el.duration * 1000) }));
    }
    syncAngle(current, currentRef.current, playingRef.current, true, false);
  };

  return {
    sortedAngles,
    lengthMs,
    audioAngle,
    audioSource: recording,
    currentMs,
    playing,
    tiles,
    videoRefs,
    setTile,
    play,
    pause,
    pauseExact,
    seek,
    isPlaying,
    getTimeMs,
    covers,
    soundKeyAt,
    refresh,
    handleLoadedMetadata,
    retryAngle,
    audioSourceRef,
    handleAudioSourceLoadedMetadata,
  };
}
